// packages/shared-utils/src/schema/game.schema.ts
import { z } from 'zod';
import { PlayerCharacterSchema } from './character.schema.js';
import { HexEdge, TerrainType, FeatureType } from "../hex/types.js";
import { AppliedEffectSchema, EncounterSchema } from "./encounter.schema.js";
import { CombatStateSchema } from "./combat.schema.js";
import { PendingLootSchema } from "./loot.schema.js";
import { ItemInstanceDTOSchema } from "./item.schema.js";
import { DiscoveryStateSchema, PlaceDTOSchema } from "./place.schema.js";
import { QuestDTOSchema } from "./quest.schema.js";
export const TerrainTypeSchema = z.nativeEnum(TerrainType);
export const FeatureTypeSchema = z.nativeEnum(FeatureType);
export const HexEdgeSchema = z.number().int().min(0).max(HexEdge.ALL);
export const HexCoordinateSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
});
export const HexSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
    terrain: TerrainTypeSchema,
    feature: FeatureTypeSchema,
    rivers: HexEdgeSchema,
    roads: HexEdgeSchema,
});
export const MapSchema = z.object({
    seed: z.string(),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
    hexes: z.array(HexSchema),
});
export const GameStateSchema = z.object({
    id: z.string(),
    characterId: z.string(),
    map: MapSchema.nullable(),
    character: PlayerCharacterSchema.nullable(),
    day: z.number().int().min(1),
    movePoints: z.number().int().min(0),
    food: z.number().int().min(0),
    gold: z.number().int().min(0),
    maxDays: z.number().int().min(1),
    status: z.enum(['active', 'ended']).default('active'),
    location: z.object({
        x: z.number().int(),
        y: z.number().int(),
        enterEdge: HexEdgeSchema
    }),
    npcIds: z.array(z.string()).default([]),
    hiredNpcIds: z.array(z.string()).default([]),
    activeCombat: CombatStateSchema.optional(),
    activeEncounter: EncounterSchema.optional(),
    pendingLoot: PendingLootSchema.optional(),
});
export const DiscoveredNpcSchema = z.object({
    id: z.string(),
    name: z.string(),
    archetypeId: z.string(),
    pronouns: z.string().optional(),
    reputation: z.string().optional(),
    playerExposedDescription: z.string().optional(),
    discoveryState: DiscoveryStateSchema.optional(),
    x: z.number().int().optional(),
    y: z.number().int().optional(),
});
export const GameDiscoveriesResponseSchema = z.object({
    npcs: z.array(DiscoveredNpcSchema),
    // Discovered quest items (known/located), as enriched instances.
    items: z.array(ItemInstanceDTOSchema),
    places: z.array(PlaceDTOSchema),
});
// Request payloads used by server routes
export const CreateMapRequestSchema = z.object({
    gameId: z.string(),
    seed: z.union([z.string(), z.number()]).optional(),
});
// Partials are shallow. This is fine for now - let's wait for a use case to
// surface before solving for character/map partials within.
export const GameStateDeltaSchema = GameStateSchema.partial();
export const MoveRequestSchema = z.object({
    gameId: z.string(),
    x: z.number().int(),
    y: z.number().int(),
    // Cheat flag: teleport to any hex, bypassing distance/adjacency/travel-cost checks.
    // Available to all players during early development.
    cheatTeleport: z.boolean().optional(),
});
export const MoveResponseSchema = z.object({
    stateDelta: GameStateDeltaSchema,
    encounter: EncounterSchema.optional(),
    combat: CombatStateSchema.optional(),
    appliedEffects: z.array(AppliedEffectSchema).optional(),
    discoveredPlaces: z.array(z.string()).optional(),
    quest: QuestDTOSchema.optional(),
    pendingLoot: PendingLootSchema.optional(),
});
// Using a consumable on the map. `targetHex` is supplied only by effects that need a
// destination (the teleport handler); every other consumable ignores it.
export const UseItemRequestSchema = z.object({
    gameId: z.string(),
    instanceId: z.string(),
    targetHex: HexCoordinateSchema.optional(),
});
// A use can move the player (teleport), so the response carries everything a move
// does — encounter, combat, discoveries, loot — plus the item's flavor line.
export const UseItemResponseSchema = MoveResponseSchema.extend({
    useText: z.string(),
});
export const RestRequestSchema = z.object({ gameId: z.string() });
export const HirelingHealEntrySchema = z.object({
    npcId: z.string(),
    npcName: z.string(),
    fed: z.boolean(),
    hpRestored: z.number().int().min(0),
    mpRestored: z.number().int().min(0),
});
export const RestSummarySchema = z.object({
    restType: z.enum(['wilderness', 'inn']),
    foodConsumed: z.number().int().min(0),
    goldSpent: z.number().int().min(0),
    hpRestored: z.number().int().min(0),
    mpRestored: z.number().int().min(0),
    newDay: z.number().int().min(1),
    hirelingHeals: z.array(HirelingHealEntrySchema).optional(),
});
export const RestResponseSchema = z.object({
    stateDelta: GameStateDeltaSchema,
    restSummary: RestSummarySchema,
    gameOver: z.boolean().optional(),
    encounter: EncounterSchema.optional(),
    combat: CombatStateSchema.optional(),
    appliedEffects: z.array(AppliedEffectSchema).optional(),
});
// Combat resolution response (lives here to avoid circular imports between combat and encounter schemas)
export const CombatResolveResponseSchema = GameStateSchema.extend({
    encounter: EncounterSchema.optional(),
});
// Summary of a game for use in the lobby roster (lightweight, no map data)
export const GameSummarySchema = z.object({
    id: z.string(),
    characterId: z.string(),
    day: z.number().int().min(1),
    maxDays: z.number().int().min(1),
    status: z.enum(['active', 'ended']),
    createdAt: z.string(),
});
// Character roster entry — a player character plus its active game (if any)
export const CharacterRosterEntrySchema = PlayerCharacterSchema.extend({
    activeGame: GameSummarySchema.nullable(),
});
